class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Ensure any newly-inserted cart-recommendations elements initialize
    try {
      const recomEls = document.querySelectorAll('cart-recommendations');
      recomEls.forEach((el) => {
        if (el && typeof el.fetchRecommendations === 'function') {
          try { el.fetchRecommendations(); } catch (e) { console.error(e); }
        }
      });
    } catch (e) {
      console.error('Error initializing cart-recommendations:', e);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Ensure cart recommendations custom element is defined globally so it survives
// AJAX replacement of the cart drawer HTML.
if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', class extends HTMLElement {
      connectedCallback() {
        // Always attempt to fetch recommendations when connected. We guard
        // against redundant requests by tracking last fetched product id.
        this.fetchRecommendations();
      }

    fetchRecommendations() {
      const productId = this.dataset.productId;
      const slider = this.querySelector('.recom-slider');
      if (!productId || !slider) return;

      // local helper to render safe placeholders if API calls are blocked
      const renderPlaceholders = (targetSlider) => {
        try {
          targetSlider.innerHTML = '';
          for (let i = 0; i < 4; i++) {
            const card = document.createElement('a');
            card.className = 'recom-card';
            card.href = '/collections/all';
            card.innerHTML = `
              <img src="//cdn.shopify.com/s/files/1/placeholder-50x50.png" class="recom-card-img" alt="Polecane produkty">
              <div class="recom-card-info">
                <span class="recom-card-title">Sprawdź naszą ofertę</span>
                <span class="recom-card-price">Zobacz</span>
              </div>
              <button type="button" class="recom-card-add">Zobacz</button>
            `;
            targetSlider.appendChild(card);
          }
        } catch (e) {
          console.error('Failed to render placeholders for recommendations', e);
          // as last resort, hide element
          try { this.style.display = 'none'; } catch (er) { /* swallow */ }
        }
      };

      const url = window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + `recommendations/products.json?product_id=${productId}&limit=4&intent=related`
        : `/recommendations/products.json?product_id=${productId}&limit=4&intent=related`;

      // show loading text if present
      const loading = slider.querySelector('.recom-loading');
      if (loading) loading.style.display = 'block';

      console.debug('Cart Recommendations: fetching', url);

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('API response not ok');
          return response.json();
        })
        .then(({ products }) => {
          if (loading) loading.style.display = 'none';
          if (products && products.length > 0) {
            slider.innerHTML = '';
            products.forEach((product) => {
              const hasVariants = product.variants && product.variants.length > 1;
              const buttonText = hasVariants ? 'Wybierz' : '+ Dodaj';
              const imageUrl = product.featured_image || (product.images && product.images[0]) || '';

              const card = document.createElement('a');
              card.className = 'recom-card';
              card.href = product.url || '#';

              card.innerHTML = `
                <img src="${imageUrl}" class="recom-card-img" alt="${(product.title||'').replace(/"/g, '&quot;')}">
                <div class="recom-card-info">
                  <span class="recom-card-title">${product.title}</span>
                  <span class="recom-card-price">${Shopify && Shopify.formatMoney ? Shopify.formatMoney(product.price) : (product.price/100).toFixed(2) + ' zł'}</span>
                </div>
                <button type="button" class="recom-card-add">${buttonText}</button>
              `;

              const btn = card.querySelector('.recom-card-add');
              if (!hasVariants && product.variants && product.variants[0]) {
                btn.addEventListener('click', (e) => {
                  e.preventDefault();
                  window.addRecomToCart(btn, product.variants[0].id);
                });
              } else {
                btn.addEventListener('click', (e) => {
                  // go to product page for selection
                  e.preventDefault();
                  window.location.href = product.url;
                });
              }

              slider.appendChild(card);
            });
          } else {
            // Fallback to collections/all/products.json to ensure UI shows something
            console.warn('Cart Recommendations: No products from recommendations API, falling back to collection list.');
            fetch(`/collections/all/products.json?limit=4`)
              .then((r) => r.ok ? r.json() : Promise.reject(new Error('Fallback response not ok')))
              .then(({ products: fallbackProducts }) => {
                if (loading) loading.style.display = 'none';
                if (fallbackProducts && fallbackProducts.length > 0) {
                  slider.innerHTML = '';
                  fallbackProducts.forEach((product) => {
                    const imageUrl = product.featured_image || (product.images && product.images[0]) || '';
                    const card = document.createElement('a');
                    card.className = 'recom-card';
                    card.href = product.url || '#';
                    card.innerHTML = `
                      <img src="${imageUrl}" class="recom-card-img" alt="${(product.title||'').replace(/"/g, '&quot;')}">
                      <div class="recom-card-info">
                        <span class="recom-card-title">${product.title}</span>
                        <span class="recom-card-price">${Shopify && Shopify.formatMoney ? Shopify.formatMoney(product.price) : (product.price/100).toFixed(2) + ' zł'}</span>
                      </div>
                      <button type="button" class="recom-card-add">+ Dodaj</button>
                    `;
                    const btn = card.querySelector('.recom-card-add');
                    if (product.variants && product.variants[0]) {
                      btn.addEventListener('click', (e) => { e.preventDefault(); window.addRecomToCart(btn, product.variants[0].id); });
                    }
                    slider.appendChild(card);
                  });
                } else {
                  console.warn('Cart Recommendations fallback also returned no products. Showing placeholders.');
                  renderPlaceholders(slider);
                }
              }).catch((err) => {
                if (loading) loading.style.display = 'none';
                console.error('Cart Recommendations fallback error:', err);
                renderPlaceholders(slider);
              });
          }
        })
        .catch((err) => {
          if (loading) loading.style.display = 'none';
          console.error('Cart Recommendations fetch error:', err);
          renderPlaceholders(slider);
        });
    }
  });

  // AJAX add to cart for recommendation buttons
  window.addRecomToCart = window.addRecomToCart || function (buttonEl, variantId) {
    if (buttonEl) buttonEl.classList.add('loading');

    const config = fetchConfig('javascript');
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', 'cart-drawer,cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add.js', config)
      .then((res) => res.json())
      .then((response) => {
        if (buttonEl) buttonEl.classList.remove('loading');
        if (response.status) {
          console.error('Error adding recommendation to cart:', response.description || response);
          return;
        }

        // If cart drawer component exists, try to re-render using its API
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(response);
        } else {
          window.location.reload();
        }
      })
      .catch((err) => {
        if (buttonEl) buttonEl.classList.remove('loading');
        console.error(err);
      });
  };
}
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
